import { useEffect, useState } from 'react';

// Compile-time values injected by apps/web/vite.config.ts via `define`.
// Declared here so TypeScript accepts the references; the runtime values
// are baked into the bundle by Rollup.
declare const __CIPHEROPS_GIT_SHA__: string;
declare const __CIPHEROPS_GIT_BRANCH__: string;
declare const __CIPHEROPS_BUILD_TIME__: string;

interface ServerVersion {
  service: string;
  git_sha: string;
  git_branch: string;
  build_time: string | null;
  started_at: string;
  node_env: string;
}

// VersionBanner — a small bottom-right chip showing `branch@sha` of the
// currently-loaded client bundle. Click to expand a tooltip showing the
// server's /version response too, so the operator can confirm at a glance
// that client and server are running the SAME commit.
//
// Why this exists: drift between localhost and live (or between web and
// api on the same surface) used to be invisible until a bug surfaced.
// This chip makes it eyeball-detectable in one second.
export function VersionBanner() {
  const [expanded, setExpanded] = useState(false);
  const [server, setServer] = useState<ServerVersion | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const clientSha = typeof __CIPHEROPS_GIT_SHA__ === 'string' ? __CIPHEROPS_GIT_SHA__ : 'dev';
  const clientBranch =
    typeof __CIPHEROPS_GIT_BRANCH__ === 'string' ? __CIPHEROPS_GIT_BRANCH__ : 'dev';
  const clientBuildTime =
    typeof __CIPHEROPS_BUILD_TIME__ === 'string' ? __CIPHEROPS_BUILD_TIME__ : '';

  useEffect(() => {
    if (!expanded || server || serverError) return;
    let cancelled = false;
    fetch('/api/v1/version', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: ServerVersion) => {
        if (!cancelled) setServer(data);
      })
      .catch((err: Error) => {
        if (!cancelled) setServerError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, server, serverError]);

  const driftWarning =
    server && server.git_sha !== 'dev' && clientSha !== 'dev' && server.git_sha !== clientSha;

  return (
    <div
      style={{
        position: 'fixed',
        right: 8,
        bottom: 8,
        zIndex: 9999,
        fontFamily: '"Courier New", monospace',
        fontSize: 10,
        userSelect: 'none',
        pointerEvents: 'auto',
      }}
    >
      {expanded && (
        <div
          style={{
            background: '#ffffff',
            border: `1px solid ${driftWarning ? '#c44a1a' : '#d0ccc4'}`,
            borderRadius: 0,
            padding: '10px 14px',
            marginBottom: 4,
            minWidth: 280,
            maxWidth: 360,
            color: '#1a1f2e',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontFamily: '"Courier New", monospace',
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: '#7a7d8a',
              marginBottom: 6,
            }}
          >
            Build identity
          </div>
          <Row label="client branch" value={clientBranch} />
          <Row label="client sha" value={clientSha} />
          {clientBuildTime && <Row label="client built" value={clientBuildTime} />}
          <div style={{ borderTop: '1px solid #e0ddd8', margin: '6px 0' }} />
          {server ? (
            <>
              <Row label="server branch" value={server.git_branch} />
              <Row label="server sha" value={server.git_sha} />
              {server.build_time && <Row label="server built" value={server.build_time} />}
              <Row label="server started" value={server.started_at} />
              <Row label="server env" value={server.node_env} />
            </>
          ) : serverError ? (
            <div style={{ color: '#c44a1a', fontSize: 10 }}>
              server /version unreachable — {serverError}
            </div>
          ) : (
            <div style={{ color: '#7a7d8a', fontSize: 10 }}>loading server /version…</div>
          )}
          {driftWarning && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid #e0ddd8',
                color: '#c44a1a',
                fontFamily: 'Georgia, serif',
                fontSize: 11,
                fontStyle: 'italic',
              }}
            >
              ⚠ Client and server are on different commits. The browser tab is
              loading bundle <code>{clientSha}</code> but the API is running{' '}
              <code>{server!.git_sha}</code>.
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        // a11y 2026-06-30: this chip is a disclosure toggle for the build-identity
        // panel above it. aria-expanded lets a screen reader announce collapsed/
        // expanded state; the visible `branch@sha` text is already the name.
        aria-expanded={expanded}
        aria-label={`Build identity ${clientBranch}@${clientSha} — ${expanded ? 'hide' : 'show'} details`}
        style={{
          background: '#eae8e2',
          border: '1px solid #d0ccc4',
          borderRadius: 0,
          padding: '3px 8px',
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          color: '#4a5568',
          cursor: 'pointer',
          opacity: expanded ? 1 : 0.55,
          transition: 'opacity 120ms ease-in-out',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = expanded ? '1' : '0.55')}
        title="Build identity — click for details"
      >
        {clientBranch}@{clientSha}
      </button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 10, marginBottom: 2 }}>
      <span style={{ color: '#7a7d8a', minWidth: 100 }}>{label}</span>
      <span style={{ color: '#1a1f2e', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}
